import { useState, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { usePedidoWhatsApp } from '@/hooks/usePedidoWhatsApp';
import SelectorVendedor from './SelectorVendedor';

function buildWhatsAppMessage(cart: ReturnType<typeof useCart>['cart']): string {
  const lines = cart.items.map(
    (item) =>
      `• ${item.product.name} x${item.quantity} — $${(item.product.price * item.quantity).toFixed(2)} USD`,
  );
  const msg = [
    '¡Hola! Quiero hacer este pedido:',
    '',
    ...lines,
    '',
    `Total: $${cart.total.toFixed(2)} USD`,
    '',
    '¿Me pueden confirmar disponibilidad y coordinar el envío?',
  ].join('\n');
  return encodeURIComponent(msg);
}

export default function WhatsAppCartButton() {
  const { cart } = useCart();
  const pedido = usePedidoWhatsApp();
  const [visible, setVisible] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [ping, setPing] = useState(false);

  const hasItems = cart.itemCount > 0;

  // Entrada suave al aparecer
  useEffect(() => {
    if (hasItems) {
      const t = setTimeout(() => setVisible(true), 80);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
      setShowLabel(false);
    }
  }, [hasItems]);

  // Muestra la etiqueta automáticamente al aparecer
  useEffect(() => {
    if (visible) {
      const t1 = setTimeout(() => setShowLabel(true), 500);
      const t2 = setTimeout(() => setShowLabel(false), 4000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [visible]);

  // Ping (onda) cada vez que cambia la cantidad
  useEffect(() => {
    if (hasItems) {
      setPing(true);
      const t = setTimeout(() => setPing(false), 700);
      return () => clearTimeout(t);
    }
  }, [cart.itemCount, hasItems]);

  const handleWhatsApp = () => {
    pedido.enviar(buildWhatsAppMessage(cart));
  };

  if (!hasItems) return null;

  return (
    <>
      <style>{`
        @keyframes floatBounce {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes pingRing {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes waShimmer {
          0%   { transform: translateX(-120%); }
          60%  { transform: translateX(120%); }
          100% { transform: translateX(120%); }
        }
      `}</style>

      <div
        className="fixed z-40"
        style={{
          bottom: '24px',
          right: '20px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.6) translateY(24px)',
          transition: 'opacity 0.4s cubic-bezier(0.34,1.56,0.64,1), transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        {/* Etiqueta flotante */}
        <div
          className="absolute right-[68px] top-1/2 -translate-y-1/2 flex items-center gap-2 px-3.5 py-2.5 rounded-2xl whitespace-nowrap"
          style={{
            background: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.10)',
            backdropFilter: 'blur(14px)',
            opacity: showLabel ? 1 : 0,
            transform: showLabel ? 'translateX(0) scale(1)' : 'translateX(10px) scale(0.95)',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
            pointerEvents: 'none',
          }}
        >
          <i className="ri-shopping-bag-3-line text-emerald-400 text-sm" />
          <div className="flex flex-col leading-tight">
            <span className="text-white text-[12px] font-bold">
              {cart.itemCount} {cart.itemCount === 1 ? 'producto' : 'productos'}
            </span>
            <span className="text-emerald-400 text-[10px] font-semibold">Pedir por WhatsApp</span>
          </div>
          {/* Flecha derecha */}
          <div
            className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rotate-45"
            style={{
              background: '#1a1a1a',
              borderTop: '1px solid rgba(255,255,255,0.10)',
              borderRight: '1px solid rgba(255,255,255,0.10)',
            }}
          />
        </div>

        {/* Onda de ping */}
        {ping && (
          <div
            className="absolute inset-0 rounded-full"
            style={{
              animation: 'pingRing 0.7s ease-out forwards',
              background: 'rgba(37,211,102,0.35)',
            }}
          />
        )}

        {/* Botón principal */}
        <button
          onClick={handleWhatsApp}
          onMouseEnter={() => setShowLabel(true)}
          onMouseLeave={() => setShowLabel(false)}
          aria-label={`Pedir ${cart.itemCount} producto${cart.itemCount !== 1 ? 's' : ''} por WhatsApp`}
          className="relative flex items-center justify-center rounded-full cursor-pointer"
          style={{
            width: '60px',
            height: '60px',
            background: 'linear-gradient(145deg, #2edb6e 0%, #25d366 45%, #128c7e 100%)',
            boxShadow: '0 4px 20px rgba(37,211,102,0.40), 0 2px 8px rgba(0,0,0,0.30)',
            animation: 'floatBounce 2.4s ease-in-out infinite',
          }}
        >
          {/* Shimmer wrapper — overflow-hidden aquí para no cortar el badge */}
          <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%)',
                animation: 'waShimmer 3s ease-in-out infinite',
              }}
            />
          </div>

          {/* Icono */}
          <i className="ri-whatsapp-line text-white relative z-10" style={{ fontSize: '26px' }} />

          {/* Badge cantidad */}
          <span
            className="absolute flex items-center justify-center rounded-full text-white font-black"
            style={{
              top: '-2px',
              right: '-2px',
              minWidth: '20px',
              height: '20px',
              fontSize: '10px',
              padding: '0 4px',
              background: '#ef4444',
              border: '2px solid #141414',
              lineHeight: 1,
            }}
          >
            {cart.itemCount > 9 ? '9+' : cart.itemCount}
          </span>
        </button>
      </div>

      <SelectorVendedor {...pedido.selector} />
    </>
  );
}
